import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { CoordalIcon, GhanaPostIcon } from '../ui/PostcodeIcons';

interface CoordalPostcodeResultProps {
  fullCode: string;
  shortCode: string;
}

export function CoordalPostcodeResult({ fullCode, shortCode }: CoordalPostcodeResultProps) {
  const [copiedKind, setCopiedKind] = useState<'full' | 'short' | null>(null);
  const [copiedChar, setCopiedChar] = useState<string | null>(null);

  const displayCode = fullCode || shortCode;
  if (!displayCode) return null;

  const copyText = async (text: string, kind: 'full' | 'short' | 'char', key?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (kind === 'char' && key) {
        setCopiedChar(key);
        setTimeout(() => setCopiedChar(null), 1200);
      } else if (kind === 'full' || kind === 'short') {
        setCopiedKind(kind);
        setTimeout(() => setCopiedKind(null), 2000);
      }
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <CoordalIcon size={20} className="shrink-0" />
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Coordal Postcode</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            Complete Coordal code. Use for deliveries and official records.
          </p>
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-1.5"
        role="group"
        aria-label={`Coordal postcode: ${displayCode}`}
      >
        {displayCode.split('').map((char, index) => {
          if (char === ' ') {
            return <span key={`space-${index}`} className="w-2 shrink-0" aria-hidden />;
          }
          const key = `${index}-${char}`;
          const isCopied = copiedChar === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => void copyText(char, 'char', key)}
              className={`min-w-[2rem] h-10 px-1.5 rounded-lg border font-mono text-base font-bold transition-all ${
                isCopied
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-600'
                  : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
              }`}
              title={`Copy “${char}”`}
            >
              {char}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        {shortCode && fullCode && shortCode !== fullCode ? (
          <>
            <button
              type="button"
              onClick={() => void copyText(shortCode, 'short')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {copiedKind === 'short' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
              {copiedKind === 'short' ? 'Copied' : 'Copy short code'}
            </button>
            <button
              type="button"
              onClick={() => void copyText(fullCode, 'full')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {copiedKind === 'full' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
              {copiedKind === 'full' ? 'Copied' : 'Copy full code'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => void copyText(fullCode || shortCode, 'full')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {copiedKind === 'full' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
            {copiedKind === 'full' ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        You can search this postcode in Coordal Maps to open the full address, or use it with services
        that integrate Coordal APIs to retrieve location details.
      </p>
    </div>
  );
}

interface GhanaPostCodeResultProps {
  code: string;
}

export function GhanaPostCodeResult({ code }: GhanaPostCodeResultProps) {
  const [copied, setCopied] = useState(false);
  const [copiedChar, setCopiedChar] = useState<string | null>(null);

  if (!code) return null;

  const copyText = async (text: string, kind: 'all' | 'char', key?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (kind === 'all') {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else if (key) {
        setCopiedChar(key);
        setTimeout(() => setCopiedChar(null), 1200);
      }
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <GhanaPostIcon size={20} className="shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">GhanaPost GPS</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">National GPS address where available.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void copyText(code, 'all')}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`GhanaPost GPS: ${code}`}>
        {code.split('').map((char, index) => {
          if (char === ' ') {
            return <span key={`space-${index}`} className="w-2 shrink-0" aria-hidden />;
          }
          const key = `${index}-${char}`;
          const isCopied = copiedChar === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => void copyText(char, 'char', key)}
              className={`min-w-[2rem] h-10 px-1.5 rounded-lg border font-mono text-base font-bold transition-all ${
                isCopied
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-600'
                  : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10'
              }`}
              title={`Copy “${char}”`}
            >
              {char}
            </button>
          );
        })}
      </div>
    </div>
  );
}
